import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';

const BASE = '/documentation/views';

// Fire-and-forget: record that the current user viewed a documentation entry.
// Failures are swallowed — tracking must never disrupt the page.
export const recordDocView = createAsyncThunk(
  'docView/record',
  async ({ docType, docKey = '', docTitle = null }) => {
    try {
      await apiAxios.post(BASE, { doc_type: docType, doc_key: docKey, doc_title: docTitle });
    } catch (e) {
      // silent — non-critical analytics write
    }
    return true;
  }
);

// Admin: per-user, per-day view statistics for a doc type.
export const fetchDocViewStats = createAsyncThunk(
  'docView/fetchStats',
  async ({ docType, startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (docType) params.doc_type = docType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await apiAxios.get(`${BASE}/stats`, { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

// Admin: server-side (AG Grid) paginated/sorted/filtered detail rows.
// Returns { rows, lastRow }; the component owns the grid state, so this thunk
// just fetches (no slice state needed).
export const fetchDocViewLogs = createAsyncThunk(
  'docView/fetchLogs',
  async ({ docType, startDate, endDate, startRow, endRow, sortModel, filterModel } = {}, { rejectWithValue }) => {
    try {
      const params = { startRow, endRow };
      if (docType) params.doc_type = docType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (sortModel?.length) params.sortModel = sortModel;
      if (filterModel && Object.keys(filterModel).length) params.filterModel = filterModel;
      const res = await apiAxios.get(`${BASE}/logs`, { params });
      return res.data; // { rows, lastRow }
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

const initialState = {
  stats: null,      // { range, summary }
  loading: false,
  error: null,
};

const docViewSlice = createSlice({
  name: 'docView',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocViewStats.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchDocViewStats.fulfilled, (s, a) => { s.loading = false; s.stats = a.payload; })
      .addCase(fetchDocViewStats.rejected, (s, a) => { s.loading = false; s.error = a.payload; });
  },
});

export default docViewSlice.reducer;
